import { useState, useEffect, useCallback } from 'react';
import { surveyAPI } from '../services/api';

/**
 * Custom hook for managing survey state and logic
 * @param {string} district - District shortcode (e.g., 'garland')
 */
const useSurvey = (district) => {
  // Survey flow state
  const [surveyStage, setSurveyStage] = useState('intro'); // intro, studentID, survey, clusterSelection, results, chat
  const [currentStep, setCurrentStep] = useState(1);
  const [studentID, setStudentID] = useState('');

  // Survey data
  const [questions, setQuestions] = useState(null);
  const [responses, setResponses] = useState({});

  // AI Recommendations
  const [llmRecommendations, setLlmRecommendations] = useState([]);
  const [aiSummary, setAiSummary] = useState('');
  const [selectedClusters, setSelectedClusters] = useState([]);

  // Chat session — persisted across stage navigation and page refreshes
  const [chatSession, setChatSession] = useState({ chatId: null, sessionId: null, messages: [], initialized: false });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validatingStudentID, setValidatingStudentID] = useState(false);
  const [validationInfo, setValidationInfo] = useState(null);

  // Total steps
  const totalSteps = questions?.steps?.length || 5;

  // Restore chat session from sessionStorage when student ID is known
  useEffect(() => {
    if (!studentID || !district) return;
    const key = `chat-${district}-${studentID}`;
    const stored = sessionStorage.getItem(key);
    if (stored) {
      try {
        setChatSession(JSON.parse(stored));
      } catch (e) { /* ignore corrupt data */ }
    }
  }, [studentID]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Fetch survey questions on mount
   */
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const response = await surveyAPI.getQuestions(district);
        setQuestions(response.data);
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError(err.message || 'Failed to load survey questions');
      } finally {
        setLoading(false);
      }
    };

    if (district) {
      fetchQuestions();
    }
  }, [district]);

  /**
   * Validate student ID
   */
  const validateStudentID = useCallback(async (id) => {
    if (!id || id.trim().length === 0) {
      return { valid: false, message: 'Please enter a student ID' };
    }

    // Basic format validation (alphanumeric, 5-12 characters)
    const idRegex = /^[a-zA-Z0-9]{5,12}$/;
    if (!idRegex.test(id)) {
      return {
        valid: false,
        message: 'Student ID must be 5-12 alphanumeric characters'
      };
    }

    try {
      setValidatingStudentID(true);
      const response = await surveyAPI.validateStudentID(id, district);

      // Allow retakes - just inform them they've taken it before
      if (response.exists) {
        return {
          valid: true,
          isRetake: true,
          message: 'You have previously taken this survey. Your new responses will replace the old ones.',
          ...response
        };
      }

      return { valid: true, message: '', ...response };
    } catch (err) {
      return {
        valid: false,
        message: err.message || 'Error validating student ID. Please try again.'
      };
    } finally {
      setValidatingStudentID(false);
    }
  }, [district]);

  /**
   * Start the survey (move from intro to student ID input)
   */
  const startSurvey = useCallback(() => {
    setSurveyStage('studentID');
    setError(null);
  }, []);

  /**
   * Set student ID and move to survey
   */
  const setStudentIDAndProceed = useCallback(async (id) => {
    const validation = await validateStudentID(id);

    if (!validation.valid) {
      setError(validation.message);
      setValidationInfo(null);
      return false;
    }

    // Determine if last survey was >= 1 year ago (auto-proceed without modal)
    const lastSurveyDate = validation.lastSurveyDate ? new Date(validation.lastSurveyDate) : null;
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const surveyIsStale = lastSurveyDate && lastSurveyDate <= oneYearAgo;

    // Store validation info (includes isRetake flag, message, and selected clusters)
    setValidationInfo({
      isRetake: validation.isRetake || false,
      lastSurveyDate: validation.lastSurveyDate || null,
      message: validation.message,
      studentInfo: validation.studentInfo,
      selectedClusters: validation.selectedClusters || []
    });

    setStudentID(id);

    // Proceed straight to survey if: first time, or last survey was >= 1 year ago
    if (!validation.isRetake || surveyIsStale) {
      setSurveyStage('survey');
      setCurrentStep(1);
    }
    // Otherwise: show modal (< 1 year since last survey)

    setError(null);
    return true;
  }, [validateStudentID]);

  /**
   * Toggle a response (select/deselect a question)
   */
  const toggleResponse = useCallback((questionId) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  }, []);

  /**
   * Move to next step
   */
  const nextStep = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      setError(null);
    }
  }, [currentStep, totalSteps]);

  /**
   * Move to previous step
   */
  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError(null);
    }
  }, [currentStep]);

  /**
   * Analyze survey responses with AI
   */
  const analyzeSurvey = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await surveyAPI.analyzeResponses(studentID, responses, district);

      if (response.success) {
        setLlmRecommendations(response.data.llmRecommendations);
        setAiSummary(response.data.summary);
        setSurveyStage('clusterSelection');
        return true;
      } else {
        setError(response.error || 'Failed to analyze survey');
        return false;
      }
    } catch (err) {
      // Better error message for timeout
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('The AI analysis is taking longer than expected. Please try again.');
      } else if (err.message?.includes('Network Error')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to analyze survey. Please try again.');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [studentID, responses, district]);

  /**
   * Finalize survey with user-selected clusters and optional custom career choice
   */
  const finalizeSurvey = useCallback(async (userSelectedClusters, customCareerChoice = null) => {
    try {
      setLoading(true);
      setError(null);

      const response = await surveyAPI.finalizeSurvey(
        studentID,
        responses,
        userSelectedClusters,
        llmRecommendations,
        district,
        customCareerChoice
      );

      if (response.success) {
        // Enrich selected clusters with endorsement information
        const enrichedClusters = userSelectedClusters.map(clusterName => {
          const recommendation = llmRecommendations.find(rec => rec.clusterName === clusterName);
          return {
            clusterName,
            endorsement: recommendation?.endorsement || null
          };
        });

        // Add custom career choice if provided
        if (customCareerChoice) {
          enrichedClusters.push({
            clusterName: customCareerChoice,
            endorsement: null,
            isCustom: true
          });
        }

        setSelectedClusters(enrichedClusters);
        setSurveyStage('results');
        return true;
      } else {
        setError(response.error || 'Failed to submit survey');
        return false;
      }
    } catch (err) {
      setError(err.message || 'Failed to submit survey. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [studentID, responses, llmRecommendations, district]);

  /**
   * Reset survey to start over
   */
  const resetSurvey = useCallback(() => {
    // Clear persisted chat session for this student before resetting
    if (studentID && district) {
      sessionStorage.removeItem(`chat-${district}-${studentID}`);
    }
    setSurveyStage('intro');
    setCurrentStep(1);
    setStudentID('');
    setResponses({});
    setLlmRecommendations([]);
    setAiSummary('');
    setSelectedClusters([]);
    setChatSession({ chatId: null, sessionId: null, messages: [], initialized: false });
    setError(null);
  }, [studentID, district]);

  /**
   * Get current step questions
   */
  const getCurrentStepQuestions = useCallback(() => {
    if (!questions || !questions.steps) {
      return null;
    }
    return questions.steps.find(step => step.stepNumber === currentStep);
  }, [questions, currentStep]);

  /**
   * Get progress percentage
   */
  const getProgress = useCallback(() => {
    return Math.round((currentStep / totalSteps) * 100);
  }, [currentStep, totalSteps]);

  /**
   * Count total selected responses
   */
  const getTotalSelected = useCallback(() => {
    return Object.values(responses).filter(value => value === true).length;
  }, [responses]);

  /**
   * Update chat session state and persist to sessionStorage
   */
  const updateChatSession = useCallback((update) => {
    setChatSession(prev => {
      const next = { ...prev, ...update };
      if (studentID && district) {
        sessionStorage.setItem(`chat-${district}-${studentID}`, JSON.stringify(next));
      }
      return next;
    });
  }, [studentID, district]);

  /**
   * Skip directly to results (for students who already took survey)
   */
  const skipToResults = useCallback((id, clusters) => {
    setStudentID(id);
    setSelectedClusters(clusters || []);
    setSurveyStage('results');
  }, []);

  /**
   * Skip directly to chat (for returning students who choose Pathways Advisor)
   */
  const skipToChat = useCallback((id, clusters) => {
    setStudentID(id);
    setSelectedClusters(clusters || []);
    setSurveyStage('chat');
  }, []);

  /**
   * Proceed from results page to chat page
   */
  const proceedToChat = useCallback(() => {
    setSurveyStage('chat');
  }, []);

  /**
   * Go back from chat page to results page
   */
  const backToResults = useCallback(() => {
    setSurveyStage('results');
  }, []);

  /**
   * Proceed to survey after user confirms retake
   */
  const proceedToSurvey = useCallback(() => {
    setSurveyStage('survey');
    setCurrentStep(1);
    setError(null);
  }, []);

  return {
    // Survey flow state
    surveyStage,
    currentStep,
    totalSteps,
    studentID,

    // Survey data
    questions,
    responses,
    getCurrentStepQuestions,

    // AI Results
    llmRecommendations,
    aiSummary,
    selectedClusters,

    // UI state
    loading,
    error,
    validatingStudentID,
    validationInfo,

    // Chat session
    chatSession,
    updateChatSession,

    // Actions
    startSurvey,
    setStudentIDAndProceed,
    toggleResponse,
    nextStep,
    prevStep,
    analyzeSurvey,
    finalizeSurvey,
    resetSurvey,
    skipToResults,
    skipToChat,
    proceedToSurvey,
    proceedToChat,
    backToResults,

    // Helpers
    getProgress,
    getTotalSelected,
    validateStudentID
  };
};

export default useSurvey;
